import React, { useState, useEffect } from 'react';
import s from './LayoutTres.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_SUFIXO_CURTO } from '../../brand';

// Tolerante a maiúsculas/espaços — mesmo padrão usado no resto do sistema.
const ehServico = t => String(t || '').trim().toLowerCase() === 'serviço';

/**
 * PDV — Layout 3 "Moderno E+ Gestor" (Parâmetros → PDV → Layout 3).
 * Presentacional: todo estado/handlers vêm do PDV.jsx (mesmo padrão dos
 * demais componentes extraídos). Modais continuam renderizados pelo PDV.
 */
export default function LayoutTres({
  orgData, usuario, turnoAtual,
  abrirCaixaClick, abrirTotais,
  cliente, setModalCliente, fidelidadeCliente,
  obsTexto, setModalObs,
  busca, handleBuscaChange, handleBuscaKeyDown, buscaRef, produtoPendente,
  sugestoes, idxSug, setIdxSug, selecionarProduto, setSugestoes,
  qtd, setQtd, qtdRef, precoUnit, setPrecoUnit, precoRef, totalCampo, unid,
  pdvCfg, gravarPendente, cancelarPendente,
  itens, itemIdx, setItemIdx, removerItemPorIdx, limparItens, abrirItem,
  qtdItens, totalVenda, fmtBRL,
  abrirPreVenda, abrirF3, abrirMov, verificarEAbrirVenda, setMenuOpcoes,
  mesaAtiva, abrirModalMesas, abrirPesados,
  balancaAtiva, toggleBalanca,
  online, pendentesCount, abrirPendencias,
}) {
  const { logout } = useAuth();

  // Relógio do cabeçalho
  const [agora, setAgora] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const descontoItens = itens.reduce((acc, i) =>
    acc + Math.max(0, ((i.preco_tabela ?? i.preco_unit) - i.preco_unit)) * i.qtd, 0);
  const subtotal = totalVenda + descontoItens;

  /* ── Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos) ── */
  const temServicoL3       = itens.some(i => ehServico(i.tipo));
  const agruparL3          = !!orgData?.habilitar_servicos && temServicoL3;
  const subtotalProdutosL3 = itens.filter(i => !ehServico(i.tipo)).reduce((acc, i) => acc + i.total, 0);
  const subtotalServicosL3 = itens.filter(i => ehServico(i.tipo)).reduce((acc, i) => acc + i.total, 0);

  const horaAbertura = turnoAtual?.abertura_em
    ? new Date(turnoAtual.abertura_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Sidebar abre os próprios modais do PDV (nada de guias novas):
  // Produtos → F3 (consulta), Clientes → F10; Relatórios/Configurações
  // reservados (sem ação por enquanto).
  const NAV = [
    { icone: '🛒', label: 'PDV', ativo: true },
    { icone: '📦', label: 'Produtos', atalho: 'F3', titulo: 'Consultar produtos (F3)', acao: abrirF3 },
    { icone: '👥', label: 'Clientes', atalho: 'F10', titulo: 'Selecionar cliente (F10)', acao: () => setModalCliente(true) },
    { icone: '🖩', label: 'Caixa', titulo: 'Totais em caixa', acao: abrirTotais },
    { icone: '📈', label: 'Relatórios', titulo: 'Em breve', acao: null },
    { icone: '⚙️', label: 'Configurações', titulo: 'Em breve', acao: null },
  ];

  const ACOES = [
    ...(pdvCfg?.controle_mesas_comandas
      ? [{ tecla: 'F1', label: 'Mesas/Comandas', icone: '🍽️', acao: abrirModalMesas }]
      : []),
    { tecla: 'F2',     label: 'Pré venda',       icone: '🔍', acao: abrirPreVenda },
    ...(pdvCfg?.botao_pesados
      ? [{ tecla: 'F4', label: 'Pesados', icone: '⚖️', acao: abrirPesados, desabilitado: !!mesaAtiva }]
      : []),
    { tecla: 'F3',     label: 'Busca produtos',  icone: '🛍️', acao: abrirF3 },
    { tecla: 'F7',     label: 'Movimento',       icone: '🏃', acao: abrirMov },
    { tecla: 'F10',    label: 'Cliente',         icone: '👤', acao: () => setModalCliente(true) },
    { tecla: 'F9',     label: 'Finalizar Salvar', icone: '✅', acao: verificarEAbrirVenda },
    { tecla: 'F6',     label: 'Desconto Global', icone: '🧾', acao: verificarEAbrirVenda },
    { tecla: 'Delete', label: 'Cancelar Item',   icone: '🗑️',
      acao: () => { if (itemIdx !== null) removerItemPorIdx(itemIdx); }, desabilitado: !!mesaAtiva },
  ];

  const campoPendente = produtoPendente ? s.entradaPendente : '';

  return (
    <div className={s.wrap}>
      {/* ── Sidebar ── */}
      <aside className={s.sidebar}>
        <div className={s.logo}>
          <span className={s.logoBadge}>E+</span>
          <span className={s.logoTxt}>{BRAND_SUFIXO_CURTO}</span>
        </div>
        <nav className={s.nav}>
          {NAV.map(n => (
            <button key={n.label} type="button" tabIndex={-1}
              className={`${s.navBtn} ${n.ativo ? s.navBtnAtivo : ''}`}
              title={n.titulo}
              style={!n.acao && !n.ativo ? { opacity: 0.45, cursor: 'default' } : undefined}
              onClick={n.acao || undefined}>
              <span className={s.navIcone}>{n.icone}</span>
              {n.label}
              {n.atalho && <span className={s.navAtalho}>{n.atalho}</span>}
            </button>
          ))}
          <button type="button" tabIndex={-1} className={`${s.navBtn} ${s.sair}`}
            onClick={logout}>
            <span className={s.navIcone}>↪</span>
            Sair
          </button>
        </nav>
      </aside>

      {/* ── Coluna principal ── */}
      <div className={s.main}>

        {/* Header */}
        <header className={s.header}>
          <div>
            <div className={s.headerTitulo}>PDV</div>
            <div className={s.headerSub}>• Ponto de Venda</div>
          </div>
          <div className={s.headerDir}>
            <button
              type="button"
              className={`${s.chip} ${s.chipBalanca} ${online ? s.chipBalancaAtiva : s.chipBalancaInativa}`}
              onClick={abrirPendencias}
              title={online ? 'Conectado' : 'Sem conexão — vendas ficam salvas localmente até sincronizar'}
            >
              {online ? '🟢 Online' : '🟡 Offline'}{pendentesCount > 0 ? ` — ${pendentesCount} pendente${pendentesCount > 1 ? 's' : ''}` : ''}
            </button>
            <button
              type="button"
              className={`${s.chip} ${s.chipBalanca} ${balancaAtiva ? s.chipBalancaAtiva : s.chipBalancaInativa}`}
              onClick={toggleBalanca}
              title={balancaAtiva
                ? 'Balança ligada — clique pra desligar e informar o peso na mão'
                : 'Balança desligada — peso é digitado na mão. Clique pra voltar a capturar da balança'}
            >
              ⚖️ Balança: {balancaAtiva ? 'Ligada' : 'Desligada'}
            </button>
            <span className={s.chip}>📅 {agora.toLocaleDateString('pt-BR')}</span>
            <span className={s.chip}>🕐 {agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
            <span className={`${s.chip} ${s.chipUser}`}>
              <span>👤</span>
              <span>
                <div className={s.chipUserNome}>{usuario?.nome || 'Usuário'}</div>
                <div className={s.chipUserPerfil}>Operador</div>
              </span>
            </span>
          </div>
        </header>

        {/* Cards de status */}
        <div className={s.cards}>
          <div className={s.card} onClick={abrirCaixaClick} style={{ cursor: 'pointer' }}
            title={turnoAtual ? 'Caixa aberto — clique para ver os totais' : 'Clique para abrir o caixa'}>
            <span className={s.cardIcone} style={{ background: '#dcfce7' }}>{turnoAtual ? '🔓' : '🔒'}</span>
            <span className={s.cardCorpo}>
              <div className={s.cardLabel}>Caixa</div>
              <div className={s.cardValor} style={{ color: turnoAtual ? '#16a34a' : '#dc2626' }}>
                {turnoAtual ? 'Aberto' : 'Fechado'}
              </div>
              {horaAbertura && <div className={s.cardSub}>Desde {horaAbertura}</div>}
            </span>
          </div>

          <div className={s.card}>
            <span className={s.cardIcone} style={{ background: '#dbeafe' }}>👤</span>
            <span className={s.cardCorpo}>
              <div className={s.cardLabel}>Cliente <span className={s.cardAtalho}>F10</span></div>
              <div className={s.cardValor} style={{ color: '#2563eb' }}>
                {(cliente?.nome || 'CONSUMIDOR FINAL').toUpperCase()}
              </div>
              <div className={s.cardSub}>Cód.: {cliente?.id ?? 0}</div>
              {fidelidadeCliente?.ativo && (
                <div className={s.cardPontos} title="Saldo de pontos de fidelidade deste cliente">
                  ⭐ {fidelidadeCliente.saldo_pontos} pts
                </div>
              )}
            </span>
            <button type="button" tabIndex={-1} className={s.cardEditar}
              title="Selecionar cliente (F10)" onClick={() => setModalCliente(true)}>✏️</button>
          </div>

          {pdvCfg?.controle_mesas_comandas && (
            <div className={s.card}>
              <span className={s.cardIcone} style={{ background: '#fef3c7' }}>🍽️</span>
              <span className={s.cardCorpo}>
                <div className={s.cardLabel}>Mesa</div>
                <div className={s.cardValor} style={{ color: mesaAtiva ? '#b45309' : '#888' }}>
                  {mesaAtiva ? `Mesa ${String(mesaAtiva.numero).padStart(2, '0')}` : 'Balcão'}
                </div>
                {mesaAtiva?.descricao && <div className={s.cardSub}>{mesaAtiva.descricao}</div>}
              </span>
              <button type="button" tabIndex={-1} className={s.cardEditar}
                title="Mesas/Comandas (F1)" onClick={abrirModalMesas}>✏️</button>
            </div>
          )}

          <div className={s.card}>
            <span className={s.cardIcone} style={{ background: '#ffedd5' }}>🧑‍💼</span>
            <span className={s.cardCorpo}>
              <div className={s.cardLabel}>Vendedor</div>
              <div className={s.cardValor} style={{ color: '#ea580c' }}>
                {(mesaAtiva?.comandaOperadorNome || usuario?.nome || '—').toUpperCase()}
              </div>
              <div className={s.cardSub}>Cód.: {mesaAtiva?.comandaOperadorId ?? usuario?.id ?? '—'}</div>
            </span>
            <button type="button" tabIndex={-1} className={s.cardEditar}
              title={mesaAtiva?.comandaOperadorNome
                ? `Venda importada da mesa/comanda — vendedor é quem abriu (${mesaAtiva.comandaOperadorNome}), pra manter a comissão de quem atendeu.`
                : 'O vendedor da venda é escolhido na finalização (F9), conforme o parâmetro Solicitar Vendedor'}
              onClick={() => verificarEAbrirVenda()}>✏️</button>
          </div>

          <div className={s.card}>
            <span className={s.cardIcone} style={{ background: '#ede9fe' }}>💳</span>
            <span className={s.cardCorpo}>
              <div className={s.cardLabel}>Forma de Pagamento</div>
              <div className={s.cardValor} style={{ color: '#7c3aed' }}>DINHEIRO</div>
              <div className={s.cardSub}>Definida no F9</div>
            </span>
            <button type="button" tabIndex={-1} className={s.cardEditar}
              title="Escolher forma de pagamento (F9 — Finalizar)" onClick={() => verificarEAbrirVenda()}>✏️</button>
          </div>

          <div className={s.card}>
            <span className={s.cardIcone} style={{ background: '#ccfbf1' }}>💬</span>
            <span className={s.cardCorpo}>
              <div className={s.cardLabel}>Observações</div>
              <div className={s.cardValor} style={{ color: '#0d9488', fontWeight: obsTexto ? 800 : 500 }}>
                {obsTexto ? obsTexto.slice(0, 24) + (obsTexto.length > 24 ? '…' : '') : 'Nenhuma observação'}
              </div>
            </span>
            <button type="button" tabIndex={-1} className={s.cardEditar}
              title="Observação da venda" onClick={() => setModalObs(true)}>✏️</button>
          </div>
        </div>

        {/* Corpo: busca + tabela | resumo */}
        <div className={s.corpo}>
          <div className={s.centro}>

            {/* Busca */}
            <div className={s.buscaWrap}>
              <div className={s.buscaBox}>
                <span className={s.buscaIcone}>▮▯▮</span>
                <input
                  ref={buscaRef}
                  className={s.buscaInput}
                  placeholder="Digite o código de barras, código ou descrição do produto..."
                  value={busca}
                  readOnly={!!produtoPendente}
                  onChange={handleBuscaChange}
                  onKeyDown={handleBuscaKeyDown}
                  onBlur={() => setTimeout(() => setSugestoes([]), 150)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="button" tabIndex={-1} className={s.buscaF2}
                  title="Importar pré-venda (F2)" onClick={abrirPreVenda}>F2</button>
              </div>
              {sugestoes.length > 0 && (
                <div className={s.sugestoes}>
                  {sugestoes.map((p, idx) => (
                    <div key={p.id}
                      className={`${s.sugestaoItem} ${idxSug === idx ? s.sugestaoItemAtivo : ''}`}
                      onMouseDown={() => selecionarProduto(p)}
                      onMouseEnter={() => setIdxSug(idx)}>
                      <span className={s.sugCod}>{p.codigo_interno}</span>
                      <span className={s.sugNome}>{p.nome}</span>
                      <span className={s.sugPreco}>{fmtBRL(p.preco_venda)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Qtd/Preço — necessários p/ lançamento guiado e qtd manual */}
            <div className={s.entrada}>
              <label className={s.entradaCampo}>
                Qtd.
                <input ref={qtdRef} type="number" min="0.001" step="any"
                  className={`${s.entradaInput} ${campoPendente}`}
                  value={qtd} onChange={e => setQtd(e.target.value)}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (!produtoPendente) return;
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (pdvCfg.pedir_valor_unitario) precoRef.current?.focus();
                      else gravarPendente();
                    }
                    if (e.key === 'Escape') { e.preventDefault(); cancelarPendente(); }
                  }} />
              </label>
              <label className={s.entradaCampo}>
                UN <input className={s.entradaInput} style={{ width: 52 }} readOnly tabIndex={-1} value={unid} />
              </label>
              <label className={s.entradaCampo}>
                Unitário R$
                <input ref={precoRef} type="number" min="0" step="0.01"
                  className={`${s.entradaInput} ${campoPendente}`}
                  value={precoUnit} onChange={e => setPrecoUnit(e.target.value)}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (!produtoPendente) return;
                    if (e.key === 'Enter')  { e.preventDefault(); gravarPendente(); }
                    if (e.key === 'Escape') { e.preventDefault(); cancelarPendente(); }
                  }} />
              </label>
              <label className={s.entradaCampo}>
                Total R$ <input className={s.entradaInput} readOnly tabIndex={-1} value={totalCampo} />
              </label>
            </div>

            {/* Tabela de itens */}
            <div className={s.tabelaWrap}>
              <table className={s.tabela}>
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>CÓDIGO</th>
                    <th>DESCRIÇÃO DO PRODUTO</th>
                    <th className={s.right} style={{ width: 70 }}>QTD.</th>
                    <th style={{ width: 48 }}>UN.</th>
                    <th className={s.right} style={{ width: 96 }}>UNITÁRIO (R$)</th>
                    <th className={s.right} style={{ width: 90 }}>TOTAL (R$)</th>
                    <th style={{ width: 56, textAlign: 'center' }}>AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.length === 0 && (
                    <tr><td colSpan={7} className={s.vazio}>
                      Nenhum item adicionado — bipe ou busque um produto acima
                    </td></tr>
                  )}
                  {(() => {
                    const renderLinhaL3 = (item, idx) => (
                      <tr key={idx}
                        className={itemIdx === idx ? s.linhaSel : ''}
                        onClick={() => setItemIdx(idx)}>
                        <td style={{ fontFamily: 'monospace', color: '#475569' }}>{item.codigo}</td>
                        <td style={{ fontWeight: 600 }}>{item.descricao}</td>
                        <td className={`${s.right} ${s.tdNum}`}>
                          {Number(item.qtd).toLocaleString('pt-BR', { minimumFractionDigits: 3 })}
                        </td>
                        <td>{item.un}</td>
                        <td className={s.right}>{fmtBRL(item.preco_unit).replace('R$', '').trim()}</td>
                        <td className={`${s.right} ${s.tdNum}`}>{fmtBRL(item.total).replace('R$', '').trim()}</td>
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {abrirItem && (
                            <button type="button" tabIndex={-1} title="Desconto, preço e quantidade deste item"
                              aria-label={`Editar item ${item.descricao}`}
                              style={{ marginRight: 4, padding: '0 6px', fontSize: 12, lineHeight: '16px', cursor: 'pointer',
                                border: '1px solid #94a3b8', borderRadius: 3, background: '#f1f5f9', color: '#334155' }}
                              onClick={e => { e.stopPropagation(); setItemIdx(idx); abrirItem(idx); }}>…</button>
                          )}
                          <button type="button" tabIndex={-1} className={s.btnLixeira}
                            disabled={!!mesaAtiva}
                            title={mesaAtiva ? 'Venda importada da mesa — itens não podem mais ser alterados aqui.' : 'Remover item (Delete)'}
                            onClick={e => { e.stopPropagation(); removerItemPorIdx(idx); }}>🗑</button>
                        </td>
                      </tr>
                    );
                    if (!agruparL3) return itens.map((item, idx) => renderLinhaL3(item, idx));

                    const comIdx   = itens.map((item, idx) => ({ item, idx }));
                    const produtos = comIdx.filter(({ item }) => !ehServico(item.tipo));
                    const servicos = comIdx.filter(({ item }) => ehServico(item.tipo));
                    const grupoHeader   = { textAlign: 'left', fontWeight: 700, padding: '5px 8px', background: '#eef2ff', color: '#1e3a8a' };
                    const grupoVazio    = { textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: 8 };
                    const subtotalLabel = { textAlign: 'right', fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                    const subtotalValor = { fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                    return (
                      <React.Fragment>
                        <tr><td colSpan={7} style={grupoHeader}>📦 Produtos</td></tr>
                        {produtos.length
                          ? produtos.map(({ item, idx }) => renderLinhaL3(item, idx))
                          : <tr><td colSpan={7} style={grupoVazio}>Nenhum produto.</td></tr>}
                        <tr>
                          <td colSpan={5} style={subtotalLabel}>Subtotal Produtos:</td>
                          <td colSpan={2} style={subtotalValor}>{fmtBRL(subtotalProdutosL3)}</td>
                        </tr>
                        <tr><td colSpan={7} style={grupoHeader}>🧾 Serviços</td></tr>
                        {servicos.length
                          ? servicos.map(({ item, idx }) => renderLinhaL3(item, idx))
                          : <tr><td colSpan={7} style={grupoVazio}>Nenhum serviço.</td></tr>}
                        <tr>
                          <td colSpan={5} style={subtotalLabel}>Subtotal Serviços:</td>
                          <td colSpan={2} style={subtotalValor}>{fmtBRL(subtotalServicosL3)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {/* Rodapé da tabela */}
            <div className={s.tabelaRodape}>
              <span className={s.itensBadge}>
                ITENS <strong>{qtdItens % 1 === 0 ? qtdItens : qtdItens.toFixed(3)}</strong>
              </span>
              <button type="button" tabIndex={-1} className={s.btnLimpar} onClick={limparItens}
                disabled={!!mesaAtiva}
                title={mesaAtiva ? 'Venda importada da mesa — itens não podem mais ser alterados aqui.' : undefined}>
                🗑 LIMPAR ITENS
              </button>
            </div>
          </div>

          {/* Resumo da venda */}
          <aside className={s.resumo}>
            <div className={s.resumoTitulo}>RESUMO DA VENDA</div>
            <div className={s.resumoLinha}>
              <span>SUBTOTAL</span><strong>{fmtBRL(subtotal)}</strong>
            </div>
            <div className={s.resumoLinha}>
              <span>DESCONTO</span><strong>{fmtBRL(descontoItens)}</strong>
            </div>
            {agruparL3 && (
              <>
                <div className={s.resumoLinha} style={{ fontSize: 11, color: '#666' }}>
                  <span>📦 PRODUTOS</span><strong>{fmtBRL(subtotalProdutosL3)}</strong>
                </div>
                <div className={s.resumoLinha} style={{ fontSize: 11, color: '#666' }}>
                  <span>🧾 SERVIÇOS</span><strong>{fmtBRL(subtotalServicosL3)}</strong>
                </div>
              </>
            )}
            <div className={s.totalLabel}>TOTAL GERAL</div>
            <div className={s.totalValor}>{fmtBRL(totalVenda)}</div>
            <div className={s.hintF9}>
              <span>⚠️</span>
              <span><span className={s.hintTecla}>F9</span> - Finalizar venda</span>
            </div>
          </aside>
        </div>

        {/* Barra de ações */}
        <div className={s.acoes}>
          {ACOES.map(a => (
            <button key={a.tecla} type="button" tabIndex={-1} className={s.acaoCard} onClick={a.acao}
              disabled={a.desabilitado}
              title={a.desabilitado ? 'Venda importada da mesa — itens não podem mais ser alterados aqui.' : undefined}>
              <span className={s.acaoIcone}>{a.icone}</span>
              <span className={s.acaoTexto}>
                <span className={s.acaoTecla}>{a.tecla}</span>
                {a.label}
              </span>
            </button>
          ))}
          <button type="button" tabIndex={-1} className={`${s.acaoCard} ${s.acaoVerde}`}
            title="Menu de opções (Ctrl+F12)" onClick={() => setMenuOpcoes(true)}>
            <span className={s.acaoIcone}>🎚️</span>
            <span className={s.acaoTexto}>
              <span className={s.acaoTecla}>Ctrl+F12</span>
              Outras Opções
            </span>
          </button>
        </div>

        {/* Footer */}
        <footer className={s.footer}>
          <span>
            {(orgData?.nome_fantasia || orgData?.razao_social || 'E MAIS GESTOR').toUpperCase()}
            {' '}• ERP COMPLETO PARA VAREJO E SERVIÇOS
          </span>
          <span className={s.footerDir}>
            <span>Versão 2026.1.0</span>
            <span className={s.pontoVerde} /> Conectado
          </span>
        </footer>
      </div>
    </div>
  );
}
